import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
    findOrCreatePerson,
    unifiedSearch,
    Feature,
    FeatureStatus,
    Project,
    UnifiedSearchItem,
} from '../api/client'
import debounce from 'lodash/debounce'
import LocationPickerModal, {
    LocationPickerResult,
} from './checkin/LocationPickerModal'
import PickerRow from './PickerRow'
import { computeSubtitles } from '../utils/pickerSubtitles'
import { collectionsApi } from '../services/collectionsApi'
import type { CollectionConfig, CollectionField } from '../types/collections'

/** A place picked from the inline autocomplete (always a private/saved
 *  place — Google candidates only enter via the map picker modal). The
 *  parent attaches a row on the user-global Locations feature with
 *  `reference_value = collectionItemId`. */
export interface SelectedPlace {
    collectionItemId: number
    name: string
    lat?: number
    lng?: number
    address?: string
    googlePlaceId?: string
}

/** A person picked or created from the inline autocomplete. The parent
 *  attaches a row on the user-global People feature with
 *  `reference_value = collectionItemId`. */
export interface SelectedPerson {
    collectionItemId: number
    firstName: string
    lastName?: string | null
    nickname?: string | null
    relation?: string | null
}

interface UnifiedSelectorProps {
    placeholder?: string
    selectedItems: { type: 'project' | 'feature' | 'place' | 'person'; id: number }[]
    onSelectProject: (project: Project) => void
    onSelectFeature: (feature: Feature) => void
    /** Fired for both inline private-place picks and modal picker results
     *  that resolved to a Places-collection item. */
    onSelectPlace: (place: SelectedPlace) => void
    /** Fired only for picker results that did NOT resolve to a place (the
     *  user dropped/dragged a pin without naming or addressing). The
     *  composer attaches the row as `(latitude, longitude)`. */
    onPickCoords: (coords: { latitude: number; longitude: number }) => void
    /** Fired for both inline existing-person picks and inline-create picks. */
    onSelectPerson?: (person: SelectedPerson) => void
    /** Forwarded to LocationPickerModal so freshly-created Places land in
     *  the page-level cache, and called on inline-create person flows so the
     *  same cache picks up the new Person row. Without it, the chip renders
     *  as "Person #N" until the next page-level fetch. */
    onCollectionItemReady?: (item: { id: number; data: Record<string, any> }) => void
}

const UnifiedSelector: React.FC<UnifiedSelectorProps> = ({
    placeholder = "Add project, feature, location, or person...",
    selectedItems,
    onSelectProject,
    onSelectFeature,
    onSelectPlace,
    onPickCoords,
    onSelectPerson,
    onCollectionItemReady,
}) => {
    const [query, setQuery] = useState('')
    const [searchResults, setSearchResults] = useState<UnifiedSearchItem[]>([])
    const [isSearching, setIsSearching] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [pickerOpen, setPickerOpen] = useState(false)

    const inputRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Filter out already-selected items so the same project/feature/place
    // can't be added twice via the inline selector.
    const filteredResults = searchResults.filter(item =>
        !selectedItems.some(selected => selected.type === item.type && selected.id === item.id)
    )

    // People collection config, for the differentiating-fields subtitles on
    // person rows. Fetched lazily — only once person rows actually show up —
    // so the composer doesn't pay for /api/collections until it needs to.
    const [peopleConfig, setPeopleConfig] = useState<CollectionConfig | null>(null)
    const peopleConfigRequested = useRef(false)
    const hasPersonRows = searchResults.some(item => item.type === 'person')
    useEffect(() => {
        if (!hasPersonRows || peopleConfigRequested.current) return
        peopleConfigRequested.current = true
        collectionsApi
            .list()
            .then(collections => {
                const people = collections.find(c => c.system_kind === 'people')
                if (people) setPeopleConfig(people.config)
            })
            .catch(error => {
                console.error('Failed to load People collection config:', error)
            })
    }, [hasPersonRows])

    // Subtitles for the displayed person rows (rows without a `data` payload
    // are skipped and simply get no subtitle). The composer's primary text is
    // the full name — the backend titles person rows "first last" — so the
    // collision grouping uses `{first name} {last name}` rather than People's
    // stored `{first name}` template: two Daniels with different last names
    // are already distinguishable on screen, and both name fields would be
    // redundant as subtitles. Until the config loads, fall back to the rows'
    // own field insertion order.
    const personRows = filteredResults
        .filter(item => item.type === 'person' && item.data)
        .map(item => ({ id: item.id, data: item.data! }))
    let personSubtitleConfig: CollectionConfig
    if (peopleConfig) {
        personSubtitleConfig = { ...peopleConfig, display_template: '{first name} {last name}' }
    } else {
        const fields: Record<string, CollectionField> = {}
        let position = 0
        for (const row of personRows) {
            for (const key of Object.keys(row.data)) {
                if (key === 'schema_version' || key in fields) continue
                fields[key] = { type: key === 'picture' ? 'picture' : 'string', position: position++ }
            }
        }
        personSubtitleConfig = {
            schema_version: 1,
            fields,
            display_template: '{first name} {last name}',
        }
    }
    const personSubtitles = personRows.length > 0
        ? computeSubtitles(personRows, personSubtitleConfig)
        : new Map<number | string, string>()

    // The "Pick on map…" entry is the last row of the dropdown when the
    // dropdown is open. Hide it while the user is typing — at that point
    // the autocomplete is doing its job and the picker would feel like a
    // distraction. Reappears when the input is empty/focused.
    const showPickOnMap = query.trim().length === 0
    const pickOnMapIndex = filteredResults.length // only meaningful when showPickOnMap

    // The "Create '{query}' as person" entry appears at the bottom when the
    // user is typing AND no person row already matches the query exactly
    // (case-insensitive on display name). This is the only inline-create
    // path for People — there is no separate picker modal in v1.
    const trimmedQuery = query.trim()
    const queryLower = trimmedQuery.toLowerCase()
    const exactPersonMatch = filteredResults.some(
        item => item.type === 'person' && item.title.toLowerCase() === queryLower,
    )
    const showCreatePerson =
        !!onSelectPerson && trimmedQuery.length > 0 && !exactPersonMatch
    const createPersonIndex = filteredResults.length // mutually exclusive with showPickOnMap

    // Debounced search function
    const debouncedSearch = useCallback(
        debounce(async (searchQuery: string) => {
            if (searchQuery.trim().length === 0) {
                setSearchResults([])
                return
            }

            setIsSearching(true)
            try {
                const response = await unifiedSearch(searchQuery)
                setSearchResults(response.items)
                setSelectedIndex(0)
            } catch (error) {
                console.error('Search error:', error)
                setSearchResults([])
            } finally {
                setIsSearching(false)
            }
        }, 300),
        []
    )

    useEffect(() => {
        debouncedSearch(query)
    }, [query, debouncedSearch])

    const extraRowCount = (showPickOnMap ? 1 : 0) + (showCreatePerson ? 1 : 0)
    const totalRowCount = filteredResults.length + extraRowCount

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showDropdown) return

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                if (totalRowCount === 0) return
                setSelectedIndex(prev => (prev + 1) % totalRowCount)
                break
            case 'ArrowUp':
                e.preventDefault()
                if (totalRowCount === 0) return
                setSelectedIndex(prev => (prev - 1 + totalRowCount) % totalRowCount)
                break
            case 'Enter':
                e.preventDefault()
                if (selectedIndex < filteredResults.length) {
                    selectItem(filteredResults[selectedIndex])
                } else if (showPickOnMap && selectedIndex === pickOnMapIndex) {
                    openPicker()
                } else if (showCreatePerson && selectedIndex === createPersonIndex) {
                    void createPersonInline()
                }
                break
            case 'Escape':
                setShowDropdown(false)
                setQuery('')
                break
        }
    }

    const selectItem = (item: UnifiedSearchItem) => {
        if (item.type === 'project') {
            // Convert UnifiedSearchItem to Project. Other Project fields are
            // filled in by the parent via its own Projects cache.
            const project: Project = {
                id: item.id,
                user_id: 0,
                title: item.title,
                description: item.description,
                emoji: item.emoji,
                start_date: '',
                end_date: '',
                status: 'active',
                personas: [],
                created_at: '',
                updated_at: ''
            }
            onSelectProject(project)
        } else if (item.type === 'feature') {
            const feature: Feature = {
                id: item.id,
                user_id: 0,
                title: item.title,
                description: item.description,
                unit: item.unit,
                config: {},
                status: FeatureStatus.ACTIVE,
                project_id: item.project_id,
                created_at: '',
                updated_at: ''
            }
            onSelectFeature(feature)
        } else if (item.type === 'place') {
            onSelectPlace({
                collectionItemId: item.id,
                name: item.title,
                lat: item.lat,
                lng: item.lng,
                address: item.address,
                googlePlaceId: item.google_place_id,
            })
        } else if (item.type === 'person') {
            if (onSelectPerson) {
                onSelectPerson({
                    collectionItemId: item.id,
                    firstName: item.first_name ?? item.title,
                    lastName: item.last_name ?? null,
                    nickname: item.nickname ?? null,
                    relation: item.relation ?? null,
                })
            }
        }

        setQuery('')
        setShowDropdown(false)
        setSelectedIndex(0)
    }

    const createPersonInline = async () => {
        // Split on first whitespace (matches `_split_person_name` server-side).
        // Single-token query lands entirely in `first_name`.
        const parts = trimmedQuery.split(/\s+/)
        const firstName = parts[0]
        const lastName = parts.length > 1 ? parts.slice(1).join(' ') : null
        if (!firstName) return
        try {
            const created = await findOrCreatePerson(firstName, lastName)
            if (onCollectionItemReady) {
                onCollectionItemReady({ id: created.collection_item_id, data: created.data })
            }
            if (onSelectPerson) {
                onSelectPerson({
                    collectionItemId: created.collection_item_id,
                    firstName: (created.data?.['first name'] as string) ?? firstName,
                    lastName: (created.data?.['last name'] as string) ?? lastName,
                    nickname: null,
                    relation: null,
                })
            }
            setQuery('')
            setShowDropdown(false)
            setSelectedIndex(0)
        } catch (err) {
            console.error('Failed to create person:', err)
        }
    }

    const openPicker = () => {
        setShowDropdown(false)
        setPickerOpen(true)
    }

    const handlePickerConfirm = (result: LocationPickerResult) => {
        setPickerOpen(false)
        if (result.kind === 'place') {
            onSelectPlace({
                collectionItemId: result.collectionItemId,
                name: result.name ?? '',
                lat: result.lat,
                lng: result.lng,
            })
        } else {
            onPickCoords({ latitude: result.latitude, longitude: result.longitude })
        }
    }

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setShowDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const renderItem = (item: UnifiedSearchItem, index: number) => {
        const isSelected = index === selectedIndex

        if (item.type === 'person') {
            return (
                <div
                    key={`${item.type}-${item.id}`}
                    className={`px-4 py-2 cursor-pointer ${
                        isSelected ? 'bg-gray-100' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => selectItem(item)}
                    onMouseEnter={() => setSelectedIndex(index)}
                >
                    <PickerRow
                        primary={item.title}
                        primaryClassName="font-medium"
                        // `item.relation` keeps the old "— relation" hint when
                        // the row has no `data` payload to compute from.
                        subtitle={personSubtitles.get(item.id) || item.relation || undefined}
                        usage={
                            typeof item.checkin_count === 'number'
                                ? { checkinCount: item.checkin_count, lastTaggedAt: item.last_tagged_at }
                                : undefined
                        }
                        avatarData={
                            item.data ?? {
                                'first name': item.first_name ?? item.title,
                                'last name': item.last_name ?? null,
                                nickname: item.nickname ?? null,
                            }
                        }
                        trailing={
                            <span className="ml-2 text-xs text-sky-700 bg-sky-100 px-2 py-0.5 rounded shrink-0">
                                Person
                            </span>
                        }
                    />
                </div>
            )
        }

        return (
            <div
                key={`${item.type}-${item.id}`}
                className={`px-4 py-2 cursor-pointer flex items-center justify-between ${
                    isSelected ? 'bg-gray-100' : 'hover:bg-gray-50'
                }`}
                onClick={() => selectItem(item)}
                onMouseEnter={() => setSelectedIndex(index)}
            >
                <div className="flex items-center min-w-0">
                    {item.type === 'project' && (
                        <>
                            <div className="flex items-center min-w-0">
                                {item.emoji && <span className="mr-2">{item.emoji}</span>}
                                <span className="font-medium truncate">{item.title}</span>
                            </div>
                            <span className="ml-2 text-xs text-plum bg-plum-tint px-2 py-0.5 rounded shrink-0">Project</span>
                        </>
                    )}
                    {item.type === 'feature' && (
                        <>
                            <div className="flex items-center min-w-0">
                                {item.project_title && (
                                    <>
                                        <span className="text-sm text-gray-500 truncate">
                                            {item.project_emoji && <span className="mr-1">{item.project_emoji}</span>}
                                            {item.project_title}
                                        </span>
                                        <span className="mx-2 text-gray-400">|</span>
                                    </>
                                )}
                                <span className="font-medium truncate">{item.title}</span>
                            </div>
                            <span className="ml-2 text-xs text-amber-800 bg-amber-100 px-2 py-0.5 rounded shrink-0">Feature</span>
                        </>
                    )}
                    {item.type === 'place' && (
                        <>
                            <div className="flex items-center min-w-0">
                                <span className="mr-2">📍</span>
                                <span className="font-medium truncate">{item.title}</span>
                                {item.address && (
                                    <span className="ml-2 text-xs text-gray-500 truncate">
                                        — {item.address}
                                    </span>
                                )}
                            </div>
                            <span className="ml-2 text-xs text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded shrink-0">Place</span>
                        </>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="relative">
            <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setShowDropdown(true) }}
                onKeyDown={handleKeyDown}
                onFocus={() => setShowDropdown(true)}
                placeholder={placeholder}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action pr-10"
            />

            {/* Loading indicator */}
            {isSearching && (
                <div className="absolute right-0 top-0 h-full flex items-center pr-3">
                    <div className="animate-spin h-4 w-4 border-2 border-amber-500 border-t-transparent rounded-full"></div>
                </div>
            )}

            {/* Clear button */}
            {query && !isSearching && (
                <button
                    onClick={() => setQuery('')}
                    className="absolute right-0 top-0 h-full flex items-center pr-3 text-gray-500 hover:text-gray-700"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            )}

            {/* Dropdown */}
            {showDropdown && (filteredResults.length > 0 || showPickOnMap || showCreatePerson) && (
                <div
                    ref={dropdownRef}
                    className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-auto"
                >
                    {filteredResults.map((item, index) => renderItem(item, index))}

                    {showPickOnMap && (
                        <div
                            className={`px-4 py-2 cursor-pointer border-t flex items-center justify-between ${
                                selectedIndex === pickOnMapIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
                            }`}
                            onClick={openPicker}
                            onMouseEnter={() => setSelectedIndex(pickOnMapIndex)}
                        >
                            <span className="flex items-center">
                                <span className="mr-2">🗺️</span>
                                Pick location
                            </span>
                        </div>
                    )}

                    {showCreatePerson && (
                        <div
                            className={`px-4 py-2 cursor-pointer border-t flex items-center justify-between ${
                                selectedIndex === createPersonIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
                            }`}
                            onClick={() => { void createPersonInline() }}
                            onMouseEnter={() => setSelectedIndex(createPersonIndex)}
                        >
                            <span className="flex items-center min-w-0">
                                <span className="mr-2">👤</span>
                                <span className="truncate">
                                    Create &ldquo;{trimmedQuery}&rdquo; as person
                                </span>
                            </span>
                            <span className="ml-2 text-xs text-sky-700 bg-sky-100 px-2 py-0.5 rounded shrink-0">New</span>
                        </div>
                    )}
                </div>
            )}

            <LocationPickerModal
                open={pickerOpen}
                onConfirm={handlePickerConfirm}
                onCollectionItemReady={onCollectionItemReady}
                onClose={() => setPickerOpen(false)}
            />
        </div>
    )
}

export default UnifiedSelector
